import type { ThemeConfig } from 'antd';

type ComponentsConfig = NonNullable<ThemeConfig['components']>;

/**
 * Deep-merges two antd theme configs. `override` always wins.
 *
 * Exists because the apps adopt the brand tokens component by component rather than in one
 * cut-over: an app keeps its hand-written `ThemeConfig` and layers the brand config on top,
 * so a component that has already migrated is styled by the brand tokens while everything
 * else keeps its current look.
 *
 * Merging is two levels deep and no deeper, which is exactly the shape of a `ThemeConfig`:
 * `token` is flat, and `components` is one flat object per component. Values are scalars —
 * they replace, never merge.
 */
export const mergeTheme = (base: ThemeConfig, override: ThemeConfig): ThemeConfig => {
  const components: Record<string, object | undefined> = { ...base.components };

  for (const [name, overrideTokens] of Object.entries(override.components ?? {})) {
    components[name] = { ...components[name], ...overrideTokens };
  }

  return {
    ...base,
    ...override,
    token: { ...base.token, ...override.token },
    // Per-component token bags are structurally identical but nominally distinct
    // (ButtonToken vs AlertToken vs …), so the merged record needs the assertion.
    components: components as ComponentsConfig,
  };
};
